import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../dashboard/components/Navbar';
import Sidebar from '../dashboard/components/Sidebar';
import { cotizacionesService } from '../../services/cotizaciones.service';
import { requisicionesService } from '../../services/requisiciones.service';
import { useToast } from '../../components/base/ToastContainer';
import GenerarOrdenCompraModal from './components/GenerarOrdenCompraModal';
import SmartPasteModal from './components/SmartPasteModal';

interface ItemCotizacion {
    id: string; // id_item or valid unique key
    medicamento: string; // From the concatenated string
    cantidad: number;
    precioUnitario: number;
    subtotal: number;
    unidad?: string;
}

interface ProveedorAsignado {
    id: string;
    nombre: string;
    nit: string;
}

export default function CompletarCotizacionPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cotizacion, setCotizacion] = useState<any>(null);
    const [proveedores, setProveedores] = useState<ProveedorAsignado[]>([]);
    const [items, setItems] = useState<ItemCotizacion[]>([]);
    const [activeTab, setActiveTab] = useState<string>('');
    const [precios, setPrecios] = useState<Record<string, Record<string, number>>>({});
    const [ivas, setIvas] = useState<Record<string, Record<string, number>>>({});
    const [cantidades, setCantidades] = useState<Record<string, Record<string, number>>>({});
    const [laboratorios, setLaboratorios] = useState<Record<string, Record<string, string>>>({});
    const [cums, setCums] = useState<Record<string, Record<string, string>>>({});
    const [nombresComerciales, setNombresComerciales] = useState<Record<string, Record<string, string>>>({});
    const [fletes, setFletes] = useState<Record<string, number>>({});
    const [descuentos, setDescuentos] = useState<Record<string, number>>({});
    const [plazos, setPlazos] = useState<Record<string, string>>({});

    const [tiempos, setTiempos] = useState<Record<string, string>>({});
    const [showGenerarOCModal, setShowGenerarOCModal] = useState(false);
    const [isSmartPasteModalOpen, setIsSmartPasteModalOpen] = useState(false);

    // Estado para cuadro comparativo: ID Item -> ID Proveedor seleccionado
    const [proveedorSeleccionadoPorItem, setProveedorSeleccionadoPorItem] = useState<Record<string, string>>({});

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const loadData = async () => {
        if (!id) return;
        setLoading(true);
        try {

            const allQuotes = await cotizacionesService.getAll();
            const currentQuote = allQuotes.find((c: any) => c.id === id);
            setCotizacion(currentQuote);


            const detalleData = await cotizacionesService.getDetalleCotizacion(id);
            let finalData: any[] = [];

            if (Array.isArray(detalleData)) {
                finalData = detalleData;
            } else if (detalleData && Array.isArray((detalleData as any).data)) {
                finalData = (detalleData as any).data;
            } else if (detalleData && Array.isArray((detalleData as any).items)) {
                finalData = (detalleData as any).items;
            }

            const quoteItems: ItemCotizacion[] = finalData.map((item: any) => ({
                id: item.id || item.id_item, // Fallback
                medicamento: item.medicamento,
                cantidad: Number(item.cantidad),
                // Handle alias "precioUnitario" or original "precio_unitario"
                precioUnitario: Number(item.precioUnitario !== undefined ? item.precioUnitario : (item.precio_unitario || 0)),
                subtotal: Number(item.subtotal || 0),
                unidad: 'Unidad'
            }));

            setItems(quoteItems);

            // 3. Fetch assigned providers
            const assignedProveedores = await cotizacionesService.getProveedoresAsignados(id);
            const mappedProveedores = Array.isArray(assignedProveedores) ? assignedProveedores.map((p: any) => ({
                id: String(p.proveedor_id),
                nombre: p.nombre,
                nit: p.nit
            })) : [];

            setProveedores(mappedProveedores);

            if (mappedProveedores.length > 0) {
                setActiveTab(mappedProveedores[0].id);
            }

        } catch (error) {
            console.error('Error loading data:', error);
            showToast('Error al cargar la información de la cotización', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchProviderDetails = async () => {
            if (!id || !activeTab) return;

            try {
                const response = await cotizacionesService.getDetalleProveedor(id, activeTab);

                let itemsData: any[] = [];
                let providerData: any = {};

                if (Array.isArray(response)) {
                    itemsData = response;
                } else if (response && typeof response === 'object') {
                    itemsData = Array.isArray(response.items) ? response.items : [];
                    providerData = response.body[0];
                }

                if (itemsData.length > 0) {
                    const newPrices: Record<string, number> = {};
                    const newIvas: Record<string, number> = {};
                    const newCantidades: Record<string, number> = {};
                    const newLaboratorios: Record<string, string> = {};
                    const newCums: Record<string, string> = {};
                    const newNombresComerciales: Record<string, string> = {};

                    itemsData.forEach((item: any) => {
                        const itemId = item.id_item || item.id;

                        if (itemId) {
                            newPrices[itemId] = Number(item.precioUnitario || 0);
                            newIvas[itemId] = Number(item.porcen_iva || 0);
                            newCantidades[itemId] = Number(item.cantidad || 0);
                            newLaboratorios[itemId] = item.laboratorio || '';
                            newCums[itemId] = item.codigo_cum || item.cum || '';
                            newNombresComerciales[itemId] = item.nombre_comercial || item.nombreComercial || '';
                        }
                    });

                    // Only populate state if it's completely empty for this tab.
                    // This way we don't overwrite user's unsaved edits when they switch back and forth.
                    setPrecios(prev => {
                        if (prev[activeTab] && Object.keys(prev[activeTab]).length > 0) return prev;
                        return {
                            ...prev,
                            [activeTab]: newPrices
                        };
                    });

                    setIvas(prev => {
                        if (prev[activeTab] && Object.keys(prev[activeTab]).length > 0) return prev;
                        return {
                            ...prev,
                            [activeTab]: newIvas
                        };
                    });

                    setCantidades(prev => {
                        if (prev[activeTab] && Object.keys(prev[activeTab]).length > 0) return prev;
                        return {
                            ...prev,
                            [activeTab]: newCantidades
                        };
                    });

                    setLaboratorios(prev => {
                        if (prev[activeTab] && Object.keys(prev[activeTab]).length > 0) return prev;
                        return {
                            ...prev,
                            [activeTab]: newLaboratorios
                        };
                    });

                    setCums(prev => {
                        if (prev[activeTab] && Object.keys(prev[activeTab]).length > 0) return prev;
                        return {
                            ...prev,
                            [activeTab]: newCums
                        };
                    });

                    setNombresComerciales(prev => {
                        if (prev[activeTab] && Object.keys(prev[activeTab]).length > 0) return prev;
                        return {
                            ...prev,
                            [activeTab]: newNombresComerciales
                        };
                    });
                }

                // Update provider specific fields from root object
                if (providerData) {
                    if (providerData.plazo_pago !== undefined) {
                        setPlazos(prev => prev[activeTab] !== undefined ? prev : { ...prev, [activeTab]: providerData.plazo_pago });
                    }
                    if (providerData.tiempo_entrega !== undefined) {
                        setTiempos(prev => prev[activeTab] !== undefined ? prev : { ...prev, [activeTab]: providerData.tiempo_entrega });
                    }
                    if (providerData.descuento !== undefined) {
                        setDescuentos(prev => prev[activeTab] !== undefined ? prev : { ...prev, [activeTab]: Number(providerData.descuento) });
                    }
                    if (providerData.flete !== undefined) {
                        setFletes(prev => prev[activeTab] !== undefined ? prev : { ...prev, [activeTab]: Number(providerData.flete) });
                    }
                }
            } catch (error) {
                console.error('Error fetching provider details:', error);
                // Don't show toast here to avoid spamming if it fails silently or just has no data
            }
        };

        fetchProviderDetails();
    }, [id, activeTab]);

    // Calcular el mejor precio cuando se entra al comparativo
    useEffect(() => {
        if (activeTab === 'comparativo' && items.length > 0 && proveedores.length > 0) {
            setProveedorSeleccionadoPorItem(prev => {
                const newSeleccion = { ...prev };
                let madeChanges = false;

                items.forEach(item => {
                    // Solo sugerir si no ha sido seleccionado manualmente
                    if (!newSeleccion[item.id]) {
                        let bestProvider = '';
                        let lowestPrice = Infinity;

                        proveedores.forEach(prov => {
                            const pId = prov.id;
                            const currentPrice = precios[pId]?.[item.id];

                            // Si el proveedor tiene precio, y es mayor a 0, competimos
                            if (currentPrice !== undefined && currentPrice > 0 && currentPrice < lowestPrice) {
                                lowestPrice = currentPrice;
                                bestProvider = pId;
                            }
                        });

                        if (bestProvider) {
                            newSeleccion[item.id] = bestProvider;
                            madeChanges = true;
                        }
                    }
                });

                return madeChanges ? newSeleccion : prev;
            });
        }
    }, [activeTab, items, proveedores, precios]);

    const handlePriceChange = (itemId: string, value: string) => {
        const numValue = parseFloat(value) || 0;
        setPrecios(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [itemId]: numValue
            }
        }));
    };

    const handleIvaChange = (itemId: string, value: string) => {
        const numValue = parseFloat(value) || 0;
        setIvas(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [itemId]: numValue
            }
        }));
    };

    const handleCantidadChange = (itemId: string, value: string) => {
        const numValue = parseFloat(value) || 0;
        setCantidades(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [itemId]: numValue
            }
        }));
    };

    const handleLaboratorioChange = (itemId: string, value: string) => {
        setLaboratorios(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [itemId]: value
            }
        }));
    };

    const handleCumChange = (itemId: string, value: string) => {
        setCums(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [itemId]: value
            }
        }));
    };

    const handleNombreComercialChange = (itemId: string, value: string) => {
        setNombresComerciales(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                [itemId]: value
            }
        }));
    };

    const handleProviderFieldChange = (field: 'fletes' | 'descuentos' | 'plazos' | 'tiempos', value: string) => {
        if (field === 'fletes') {
            setFletes(prev => ({ ...prev, [activeTab]: parseFloat(value) || 0 }));
        } else if (field === 'descuentos') {
            setDescuentos(prev => ({ ...prev, [activeTab]: parseFloat(value) || 0 }));
        } else if (field === 'plazos') {
            setPlazos(prev => ({ ...prev, [activeTab]: value }));
        } else if (field === 'tiempos') {
            setTiempos(prev => ({ ...prev, [activeTab]: value }));
        }
    };

    const calculateTotals = () => {
        if (!activeTab) return { subtotal: 0, totalIva: 0, total: 0 };

        let subtotal = 0;
        let totalIva = 0;

        items.forEach(item => {
            const precio = precios[activeTab]?.[item.id] !== undefined ? precios[activeTab]?.[item.id] : item.precioUnitario;
            const cantidad = cantidades[activeTab]?.[item.id] !== undefined ? cantidades[activeTab]?.[item.id] : item.cantidad;
            const ivaRate = ivas[activeTab]?.[item.id] || 0;
            const itemSubtotal = cantidad * precio;
            const itemIva = itemSubtotal * (ivaRate / 100);

            subtotal += itemSubtotal;
            totalIva += itemIva;
        });

        const flete = fletes[activeTab] || 0;
        const descuento = descuentos[activeTab] || 0;
        const total = subtotal + totalIva + flete - descuento;

        return { subtotal, totalIva, total };
    };

    const handleImportFromExcel = (importedItems: any[]) => {
        if (!activeTab) return;

        let matchedCount = 0;
        let unmappedCount = 0;

        // Clone current state correctly
        const newPrices = { ...precios };
        const newIvas = { ...ivas };
        const newCantidades = { ...cantidades };
        const newLaboratorios = { ...laboratorios };
        const newCums = { ...cums };
        const newNombresComerciales = { ...nombresComerciales };

        if (!newPrices[activeTab]) newPrices[activeTab] = {};
        if (!newIvas[activeTab]) newIvas[activeTab] = {};
        if (!newCantidades[activeTab]) newCantidades[activeTab] = {};
        if (!newLaboratorios[activeTab]) newLaboratorios[activeTab] = {};
        if (!newCums[activeTab]) newCums[activeTab] = {};
        if (!newNombresComerciales[activeTab]) newNombresComerciales[activeTab] = {};

        // Normalizar strings para hacer matching (quitar tildes, mayusculas)
        const normalizeStr = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

        importedItems.forEach((impItem) => {
            const impNombre = normalizeStr(impItem.producto || '');
            const impCum = normalizeStr(impItem.validCum?.cum || impItem.cum || '');

            // Try to find a match in the current quoting items array
            const matchedItem = items.find(uiItem => {
                // If CUM matches exactly (and if we had CUM in items, but we only have medicamento for now)

                // Compare by medication name
                const uiNombre = normalizeStr(uiItem.medicamento || '');
                // Simple inclusion or exact match. We prefer inclusion for flexibility if names differ slightly
                return uiNombre === impNombre || uiNombre.includes(impNombre) || impNombre.includes(uiNombre);
            });

            if (matchedItem) {
                // Apply values from excel to matched item for active provider
                newPrices[activeTab][matchedItem.id] = Number(impItem.precioUnitario || 0);
                newIvas[activeTab][matchedItem.id] = Number(impItem.iva || 0);

                if (impItem.cantidad) {
                    newCantidades[activeTab][matchedItem.id] = Number(impItem.cantidad);
                }

                // Extraer el laboratorio validado o el texto original
                const labName = impItem.validLaboratory?.nombre || impItem.laboratorio;
                if (labName) {
                    newLaboratorios[activeTab][matchedItem.id] = labName;
                }

                if (impItem.cum) {
                    newCums[activeTab][matchedItem.id] = impItem.cum;
                }

                if (impItem.nombreComercial) {
                    newNombresComerciales[activeTab][matchedItem.id] = impItem.nombreComercial;
                }

                matchedCount++;
            } else {
                unmappedCount++;
            }
        });

        // Set state
        setPrecios(newPrices);
        setIvas(newIvas);
        setCantidades(newCantidades);
        setLaboratorios(newLaboratorios);
        setCums(newCums);
        setNombresComerciales(newNombresComerciales);

        if (matchedCount > 0) {
            showToast(`Se cruzaron precios e IVAs para ${matchedCount} productos exitosamente.`, 'success');
        }
        if (unmappedCount > 0) {
            showToast(`Ojo: Hubo ${unmappedCount} items del Excel que no coincidieron con los productos de la cotización.`, 'warning');
        }
    };

    const handleSave = async () => {
        if (!activeTab || !id) return;

        setSaving(true);
        try {
            const currentPrices = precios[activeTab] || {};

            const fullItemsPayload = items.map(item => {
                const precio = currentPrices[item.id] !== undefined ? currentPrices[item.id] : item.precioUnitario;
                const cantidad = cantidades[activeTab]?.[item.id] !== undefined ? cantidades[activeTab]?.[item.id] : item.cantidad;
                const laboratorio = laboratorios[activeTab]?.[item.id] || '';
                const cum = cums[activeTab]?.[item.id] || '';
                const nombreComercial = nombresComerciales[activeTab]?.[item.id] || '';

                return {
                    id_item: item.id,
                    precio: precio,
                    cantidad: cantidad,
                    laboratorio: laboratorio,
                    codigo_cum: cum,
                    nombre_comercial: nombreComercial,
                    iva_porcentaje: ivas[activeTab]?.[item.id] || 0,
                    subtotal: cantidad * precio
                };
            });

            const { subtotal, totalIva, total } = calculateTotals();

            await cotizacionesService.saveCotizacionValores({
                id_cotizacion: id,
                id_proveedor: activeTab,
                items: fullItemsPayload,
                flete: fletes[activeTab] || 0,
                descuento: descuentos[activeTab] || 0,
                plazo_pago: plazos[activeTab] || '',
                tiempo_entrega: tiempos[activeTab] || '',
                subtotal_general: subtotal,
                total_iva_general: totalIva,
                total_general: total
            });

            showToast('Valores guardados exitosamente', 'success');
        } catch (error: any) {
            console.error('Error saving values:', error);
            showToast(error.message || 'Error al guardar los valores', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleGenerarOrden = async (idProveedor: string) => {
        if (!id) return;

        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const bodega = user.bodegas[0].idbodega;
            console.log(bodega);
            await cotizacionesService.generarOrdenCompra({
                id_cotizacion: id,
                id_proveedor: idProveedor,
                idbodega: bodega,
                user_crea: user.id
            });
            showToast('Orden de Compra generada exitosamente', 'success');
            setShowGenerarOCModal(false);
            navigate('/compras/ordenes'); // Redirect to orders list or similar
        } catch (error: any) {
            console.error('Error generating PO:', error);
            showToast(error.message || 'Error al generar la Orden de Compra', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerarOrdenesConsolidadas = async () => {
        if (!id) return;

        // Validar que todos los ítems tengan un proveedor seleccionado o alertar
        if (Object.keys(proveedorSeleccionadoPorItem).length === 0) {
            showToast('Debe seleccionar al menos un proveedor para un ítem.', 'error');
            return;
        }

        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const bodega = user.bodegas[0].idbodega;

            // Agrupar items por proveedor
            const itemsPorProveedor: Record<string, any[]> = {};

            Object.entries(proveedorSeleccionadoPorItem).forEach(([itemId, proveedorId]) => {
                if (!proveedorId) return;

                if (!itemsPorProveedor[proveedorId]) {
                    itemsPorProveedor[proveedorId] = [];
                }

                const itemOf = items.find(i => i.id === itemId);
                if (itemOf) {
                    const price = precios[proveedorId]?.[itemId] !== undefined ? precios[proveedorId][itemId] : itemOf.precioUnitario;
                    const cantidad = cantidades[proveedorId]?.[itemId] !== undefined ? cantidades[proveedorId][itemId] : itemOf.cantidad;
                    const porcenIva = ivas[proveedorId]?.[itemId] || 0;
                    const laboratorio = laboratorios[proveedorId]?.[itemId] || '';
                    const codigoCum = cums[proveedorId]?.[itemId] || '';
                    const nombreComercial = nombresComerciales[proveedorId]?.[itemId] || '';

                    itemsPorProveedor[proveedorId].push({
                        id_item: itemId,
                        precio: price,
                        cantidad: cantidad,
                        laboratorio: laboratorio,
                        codigo_cum: codigoCum,
                        nombre_comercial: nombreComercial,
                        iva_porcentaje: porcenIva,
                        subtotal: (cantidad * price)
                    });
                }
            });

            const payload_proveedores = Object.keys(itemsPorProveedor).map(pId => {
                return {
                    id_proveedor: pId,
                    items: itemsPorProveedor[pId],
                    flete: fletes[pId] || 0,
                    descuento: descuentos[pId] || 0,
                    plazo_pago: plazos[pId] || '0',
                    tiempo_entrega: tiempos[pId] || '0'
                }
            });

            await cotizacionesService.generarOrdenesCompraConsolidadas({
                id_cotizacion: id,
                idbodega: bodega,
                user_crea: user.id,
                proveedores_seleccionados: payload_proveedores
            });

            showToast('Órdenes de Compra generadas exitosamente', 'success');
            navigate('/compras/ordenes');
        } catch (error: any) {
            console.error('Error generando consolidadas:', error);
            showToast(error.message || 'Error al generar OCs', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />
            <div className="flex">
                <Sidebar />
                <main className="flex-1 p-6 pt-[105px] transition-all duration-300" style={{ marginLeft: 'var(--sidebar-width, 256px)' }}>
                    <div className="w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">
                                    Completar Cotización <span className="text-teal-600">{cotizacion?.numero}</span>
                                </h1>
                                <p className="text-slate-600 mt-1">
                                    {cotizacion?.fecha} - <span className={`font-medium ${cotizacion?.estado === 'Pendiente' ? 'text-yellow-600' : 'text-slate-600'}`}>{cotizacion?.estado}</span>
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/compras')}
                                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                <i className="ri-arrow-left-line mr-2"></i>
                                Volver
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <i className="ri-loader-4-line animate-spin text-3xl text-teal-600"></i>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                {/* Tabs */}
                                <div className="flex border-b border-slate-200 overflow-x-auto bg-slate-50">
                                    {proveedores.map(proveedor => (
                                        <button
                                            key={proveedor.id}
                                            onClick={() => setActiveTab(proveedor.id)}
                                            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === proveedor.id
                                                ? 'border-teal-600 text-teal-600 bg-white'
                                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                                }`}
                                        >
                                            <i className="ri-store-2-line"></i>
                                            {proveedor.nombre}
                                        </button>
                                    ))}

                                    {proveedores.length > 1 && (
                                        <button
                                            onClick={() => setActiveTab('comparativo')}
                                            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'comparativo'
                                                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                                                : 'border-transparent text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50'
                                                }`}
                                        >
                                            <i className="ri-scales-3-line text-lg"></i>
                                            Cuadro Comparativo
                                        </button>
                                    )}

                                    {proveedores.length === 0 && (
                                        <div className="p-4 text-sm text-slate-500 italic">
                                            No hay proveedores asignados a esta cotización.
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    {activeTab === 'comparativo' ? (
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-slate-800">Cuadro Comparativo de Proveedores</h3>
                                                    <p className="text-sm text-slate-500 mt-1">Selecciona el proveedor ganador para cada ítem. El sistema resalta el de menor valor automáticamente.</p>
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto rounded-lg border border-slate-200 mb-6">
                                                <table className="min-w-full divide-y divide-slate-200">
                                                    <thead className="bg-slate-50">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10 border-r border-slate-200">
                                                                Producto
                                                            </th>
                                                            <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                                Cant.
                                                            </th>
                                                            {proveedores.map(p => (
                                                                <th key={p.id} scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider border-l border-slate-200 bg-slate-100/50">
                                                                    {p.nombre}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-slate-200">
                                                        {items.map((item) => {
                                                            const minPriceProvId = Object.keys(precios).reduce((minProv, provId) => {
                                                                const p = precios[provId]?.[item.id] || Infinity;
                                                                const currentMin = precios[minProv]?.[item.id] || Infinity;
                                                                // Ignore 0 or undefined as lowest price
                                                                if (p > 0 && p < currentMin) return provId;
                                                                return minProv;
                                                            }, proveedores[0]?.id || '');

                                                            return (
                                                                <tr key={item.id} className="hover:bg-slate-50">
                                                                    <td className="px-6 py-4 text-sm text-slate-900 sticky left-0 bg-white z-10 border-r border-slate-200">
                                                                        <div className="font-medium">{item.medicamento}</div>
                                                                    </td>
                                                                    <td className="px-4 py-4 text-sm text-center text-slate-700">
                                                                        {item.cantidad}
                                                                    </td>
                                                                    {proveedores.map(prov => {
                                                                        const provPrice = precios[prov.id]?.[item.id];
                                                                        const isSelected = proveedorSeleccionadoPorItem[item.id] === prov.id;
                                                                        const isLowest = provPrice > 0 && provPrice <= (precios[minPriceProvId]?.[item.id] || Infinity);

                                                                        return (
                                                                            <td
                                                                                key={prov.id}
                                                                                className={`px-6 py-4 border-l border-slate-200 cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/70 border-indigo-200' : 'hover:bg-slate-50'}`}
                                                                                onClick={() => setProveedorSeleccionadoPorItem(prev => ({ ...prev, [item.id]: prov.id }))}
                                                                            >
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex items-center">
                                                                                        <input
                                                                                            type="radio"
                                                                                            name={`winner-${item.id}`}
                                                                                            checked={isSelected}
                                                                                            onChange={() => setProveedorSeleccionadoPorItem(prev => ({ ...prev, [item.id]: prov.id }))}
                                                                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                                                                        />
                                                                                        <span className={`ml-3 text-sm ${isSelected ? 'font-semibold text-indigo-800' : 'text-slate-900'} ${!provPrice && 'text-slate-400 italic'}`}>
                                                                                            {provPrice ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(provPrice) : 'S/C'}
                                                                                        </span>
                                                                                    </div>
                                                                                    {isLowest && provPrice > 0 && (
                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                                            Menor
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="flex justify-end pt-4 border-t border-slate-200">
                                                <button
                                                    type="button"
                                                    onClick={handleGenerarOrdenesConsolidadas}
                                                    className="px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-md"
                                                    disabled={loading || saving}
                                                >
                                                    <i className="ri-stack-line mr-2 text-lg"></i>
                                                    Generar Órdenes Consolidadas
                                                </button>
                                            </div>
                                        </div>
                                    ) : activeTab && (
                                        <>
                                            <div className="flex justify-between items-center mb-4">
                                                <h3 className="text-lg font-semibold text-slate-800">Precios y Detalles</h3>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsSmartPasteModalOpen(true)}
                                                    className="px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors flex items-center"
                                                >
                                                    <i className="ri-file-excel-line mr-2"></i> Pegar desde Excel
                                                </button>
                                            </div>

                                            <div className="overflow-x-auto rounded-lg border border-slate-200 mb-6">
                                                <table className="min-w-full divide-y divide-slate-200">
                                                    <thead className="bg-slate-50">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                                Producto
                                                            </th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-40">
                                                                Nombre Comercial
                                                            </th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-40">
                                                                Laboratorio
                                                            </th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-32">
                                                                CUM
                                                            </th>
                                                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-24">
                                                                Cant.
                                                            </th>
                                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-40">
                                                                Precio
                                                            </th>

                                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-32">
                                                                IVA %
                                                            </th>
                                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-40">
                                                                Subtotal
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-slate-200">
                                                        {items.map((item) => {
                                                            const precio = precios[activeTab]?.[item.id] !== undefined ? precios[activeTab]?.[item.id] : item.precioUnitario;
                                                            const cantidad = cantidades[activeTab]?.[item.id] !== undefined ? cantidades[activeTab]?.[item.id] : item.cantidad;
                                                            const subtotal = cantidad * precio;
                                                            return (
                                                                <tr key={item.id} className="hover:bg-slate-50">
                                                                    <td className="px-6 py-4 text-sm text-slate-900">
                                                                        {item.medicamento}
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <input
                                                                            type="text"
                                                                            value={nombresComerciales[activeTab]?.[item.id] || ''}
                                                                            onChange={(e) => handleNombreComercialChange(item.id, e.target.value)}
                                                                            className="block w-full rounded-md border-slate-300 py-1.5 focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                                                                            placeholder="Ej. Dolex"
                                                                        />
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <input
                                                                            type="text"
                                                                            value={laboratorios[activeTab]?.[item.id] || ''}
                                                                            onChange={(e) => handleLaboratorioChange(item.id, e.target.value)}
                                                                            className="block w-full rounded-md border-slate-300 py-1.5 focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                                                                            placeholder="Ej. Genfar"
                                                                        />
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <input
                                                                            type="text"
                                                                            value={cums[activeTab]?.[item.id] || ''}
                                                                            onChange={(e) => handleCumChange(item.id, e.target.value)}
                                                                            className="block w-full rounded-md border-slate-300 py-1.5 focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-slate-600 font-mono"
                                                                            placeholder="CUM"
                                                                        />
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            step="1"
                                                                            value={cantidad || ''}
                                                                            onChange={(e) => handleCantidadChange(item.id, e.target.value)}
                                                                            className="block w-full rounded-md border-slate-300 py-1.5 text-center focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                                                                            placeholder="1"
                                                                        />
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="relative rounded-md shadow-sm">
                                                                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                                <span className="text-slate-500 sm:text-sm">$</span>
                                                                            </div>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                step="1"
                                                                                value={precio || ''}
                                                                                onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                                                                className="block w-full rounded-md border-slate-300 pl-7 pr-3 py-1.5 focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-right"
                                                                                placeholder="0"
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            step="0.01"
                                                                            value={ivas[activeTab]?.[item.id] !== undefined ? ivas[activeTab][item.id] : ''}
                                                                            onChange={(e) => handleIvaChange(item.id, e.target.value)}
                                                                            className="block w-full rounded-md border-slate-300 py-1.5 focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-right"
                                                                            placeholder="0"
                                                                        />
                                                                    </td>
                                                                    <td className="px-6 py-4 text-sm text-slate-900 text-right font-medium">
                                                                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(subtotal)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        <tr className="bg-slate-50">
                                                            <td colSpan={9} className="px-6 py-4">
                                                                <div className="flex flex-col md:flex-row gap-8 justify-end">
                                                                    <div className="flex-1 max-w-lg grid grid-cols-2 gap-4">
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-slate-600 mb-1">Plazo de Pago</label>
                                                                            <input
                                                                                type="numeric"
                                                                                value={plazos[activeTab] || '0'}
                                                                                onChange={(e) => handleProviderFieldChange('plazos', e.target.value)}
                                                                                placeholder="Ej: 30"
                                                                                className="w-full rounded-md border-slate-300 py-1.5 text-sm focus:border-teal-500 focus:ring-teal-500"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-slate-600 mb-1">Tiempo de Entrega</label>
                                                                            <input
                                                                                type="numeric"
                                                                                value={tiempos[activeTab] || '0'}
                                                                                onChange={(e) => handleProviderFieldChange('tiempos', e.target.value)}
                                                                                placeholder="Ej: 1"
                                                                                className="w-full rounded-md border-slate-300 py-1.5 text-sm focus:border-teal-500 focus:ring-teal-500"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-slate-600 mb-1">Flete</label>
                                                                            <div className="relative rounded-md shadow-sm">
                                                                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                                    <span className="text-slate-500 sm:text-xs">$</span>
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    value={fletes[activeTab] || ''}
                                                                                    onChange={(e) => handleProviderFieldChange('fletes', e.target.value)}
                                                                                    placeholder="0"
                                                                                    className="w-full rounded-md border-slate-300 pl-7 py-1.5 text-sm focus:border-teal-500 focus:ring-teal-500 text-right"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-slate-600 mb-1">Descuento</label>
                                                                            <div className="relative rounded-md shadow-sm">
                                                                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                                    <span className="text-slate-500 sm:text-xs">$</span>
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    value={descuentos[activeTab] || ''}
                                                                                    onChange={(e) => handleProviderFieldChange('descuentos', e.target.value)}
                                                                                    placeholder="0"
                                                                                    className="w-full rounded-md border-slate-300 pl-7 py-1.5 text-sm focus:border-teal-500 focus:ring-teal-500 text-right"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="w-64 space-y-2">
                                                                        <div className="flex justify-between text-sm">
                                                                            <span className="text-slate-600">Subtotal:</span>
                                                                            <span className="font-medium text-slate-900">
                                                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(calculateTotals().subtotal)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex justify-between text-sm">
                                                                            <span className="text-slate-600">IVA Total:</span>
                                                                            <span className="font-medium text-slate-900">
                                                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(calculateTotals().totalIva)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex justify-between text-sm">
                                                                            <span className="text-slate-600">Flete:</span>
                                                                            <span className="font-medium text-slate-900">
                                                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(fletes[activeTab] || 0)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex justify-between text-sm border-b border-slate-300 pb-2">
                                                                            <span className="text-slate-600">Descuento:</span>
                                                                            <span className="font-medium text-red-600">
                                                                                - {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(descuentos[activeTab] || 0)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex justify-between text-lg font-bold">
                                                                            <span className="text-slate-800">Total:</span>
                                                                            <span className="text-teal-700">
                                                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(calculateTotals().total)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="flex justify-end gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowGenerarOCModal(true)}
                                                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center shadow-sm"
                                                    disabled={loading || saving}
                                                >
                                                    <i className="ri-file-list-3-line mr-2"></i>
                                                    Asignar y Generar OC
                                                </button>
                                                <button
                                                    type="button"
                                                    className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center shadow-sm"
                                                    onClick={handleSave}
                                                    disabled={saving || !activeTab}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                                                            Guardando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ri-save-line mr-2"></i>
                                                            Guardar Cotización Proveedor
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            <GenerarOrdenCompraModal
                isOpen={showGenerarOCModal}
                onClose={() => setShowGenerarOCModal(false)}
                onConfirm={handleGenerarOrden}
                proveedores={proveedores.map(p => ({ id: p.id, nombre: p.nombre }))}
            />

            <SmartPasteModal
                isOpen={isSmartPasteModalOpen}
                onClose={() => setIsSmartPasteModalOpen(false)}
                onSuccess={handleImportFromExcel}
            />
        </div >
    );
}
